import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useUser } from '../../auth/hooks';
import { Flex, Icon, theme, Typo } from '../../../ui';
import CoursesListCarousel from './CoursesListCarousel';
import PageSection from '../../../ui/Page/PageSection';

const STATUS_OPTIONS = [
  { key: 'inProgress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'expired', label: 'Expired', disabled: true },
];

const SelectorWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  margin-left: 2px;
  padding-top: 6px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: ${theme.colors.NEU_0};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 10;
  min-width: 160px;
  overflow: hidden;
`;

const DropdownOption = styled.div`
  padding: 10px 16px;
  font-family: inherit;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? '600' : '300')};
  color: ${({ $disabled }) => ($disabled ? theme.colors.NEU_300 : theme.colors.NEU_PRIMARY)};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  background: ${({ $active }) => ($active ? theme.colors.NEU_50 : 'transparent')};

  &:hover {
    background: ${({ $disabled, $active }) => ($disabled ? 'transparent' : $active ? theme.colors.NEU_50 : theme.colors.NEU_50)};
  }
`;

const MyCoursesList = ({ courses, title, searchQuery = '' }) => {
  const user = useUser();
  const userName = user?.displayName;
  const [selectedStatus, setSelectedStatus] = useState('inProgress');
  const [open, setOpen] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let filtered = courses.filter((course) => {
      if (course.canEdit) return false;
      if (selectedStatus === 'inProgress') return course.progress < 1;
      if (selectedStatus === 'completed') return course.progress === 1;
      return false;
    });

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((course) => course.name?.toLowerCase().includes(query));
    }

    setFilteredCourses(filtered);
  }, [courses, userName, selectedStatus, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    if (option.disabled) return;
    setSelectedStatus(option.key);
    setOpen(false);
  };

  const currentLabel = STATUS_OPTIONS.find((o) => o.key === selectedStatus)?.label;

  const hasAnyCourses = courses.some((course) => !course.canEdit);

  if (!hasAnyCourses && !searchQuery) return null;

  const headerContent = (
    <>
      <Typo.SectionHeader $caps fontWeight='300' color={theme.colors.PRIMARY}>
        {title}
      </Typo.SectionHeader>
      <Typo.ContentBigHeader color={theme.colors.COURSIO}>:</Typo.ContentBigHeader>
      <SelectorWrapper ref={wrapperRef} onClick={() => setOpen((prev) => !prev)}>
        <Typo.BigText>{currentLabel}</Typo.BigText>
        <Arrow $open={open}>
          <Icon.ExpandMore color={theme.colors.NEU_400} size='small' />
        </Arrow>
        {open && (
          <DropdownMenu>
            {STATUS_OPTIONS.map((option) => (
              <DropdownOption
                key={option.key}
                $active={option.key === selectedStatus}
                $disabled={option.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option);
                }}
              >
                {option.label}
              </DropdownOption>
            ))}
          </DropdownMenu>
        )}
      </SelectorWrapper>
    </>
  );

  return filteredCourses.length ? (
    <PageSection gap='20'>
      <CoursesListCarousel courses={filteredCourses} header={headerContent} />
    </PageSection>
  ) : (
    <PageSection gap='20'>
      <Flex width='100%' gap='5'>
        {headerContent}
      </Flex>
      <Typo.MediumText color={theme.colors.NEU_400}>{searchQuery ? `No matching courses for "${searchQuery}"` : `No ${currentLabel?.toLowerCase()} courses`}</Typo.MediumText>
    </PageSection>
  );
};

export default MyCoursesList;
